import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Phone } from "@phosphor-icons/react";
import Modal from "src/pages/Modals/ModalBase";
import { setShowPhoneCollectionModal } from "src/store/slices/modalSlice";
import { addPhoneNumber } from "src/store/slices/onboardingSlice";
import { trackAnalytics } from "src/pages/helpers";
import { BUTTON_ROLES, BUTTON_TYPES, EVENT_NAMES, EVENT_PARAMETERS, getPageName } from "src/utils/EventConstants";
import {
  PHONE_COLLECTION_STORAGE_KEY,
  PHONE_COLLECTION_MAX_DISMISSALS,
  PHONE_COLLECTION_CONSENT_TEXT,
  PHONE_COLLECTION_COPY,
} from "src/utils/Constants";

const PhoneCollectionModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.modalSlice.showPhoneCollectionModal);
  const addPhoneStatus = useSelector((state) => state.onboarding.addPhoneStatus);

  const dismissCount = parseInt(localStorage.getItem(PHONE_COLLECTION_STORAGE_KEY) || "0", 10);
  const copy = PHONE_COLLECTION_COPY[Math.min(dismissCount, PHONE_COLLECTION_COPY.length - 1)];

  const [phoneInput, setPhoneInput] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [validationError, setValidationError] = useState("");

  const handleClose = () => {
    const next = dismissCount + 1;
    localStorage.setItem(PHONE_COLLECTION_STORAGE_KEY, String(next));
    dispatch(setShowPhoneCollectionModal(false));

    trackAnalytics(EVENT_NAMES.PHONE_BANNER_DISMISSED, {
      [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
      [EVENT_PARAMETERS.BUTTON_TEXT]: `${copy.dismiss} (modal)`,
      [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.DISMISS_PHONE_BANNER,
      [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
      dismiss_count: next,
    });
  };

  const validatePhone = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length !== 10) return "Enter a 10-digit US phone number";
    return "";
  };

  const handleSubmit = () => {
    const error = validatePhone(phoneInput);
    if (error) {
      setValidationError(error);
      return;
    }
    if (!consentChecked) {
      setValidationError("Please consent to receive SMS updates");
      return;
    }

    setValidationError("");
    const e164 = "+1" + phoneInput.replace(/\D/g, "");

    dispatch(
      addPhoneNumber({
        phone_number: e164,
        consent_text: PHONE_COLLECTION_CONSENT_TEXT,
        consent_version: process.env.REACT_APP_CONSENT_VERSION || "v1",
        source: "phone_collection_modal",
      }),
    ).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        localStorage.setItem(PHONE_COLLECTION_STORAGE_KEY, String(PHONE_COLLECTION_MAX_DISMISSALS));
        dispatch(setShowPhoneCollectionModal(false));
        trackAnalytics(EVENT_NAMES.PHONE_NUMBER_ADDED, {
          [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
          [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.ADD_PHONE,
          [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
        });
      } else {
        const status = result.payload?.error?.response?.status;
        if (status === 422) {
          setValidationError("Please enter a valid US phone number.");
        } else {
          setValidationError("Something went wrong. Please try again.");
        }
        trackAnalytics(EVENT_NAMES.PHONE_NUMBER_ADD_FAILED, {
          [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
        });
      }
    });
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhoneInput(raw);
    if (validationError) setValidationError("");
  };

  const formatDisplay = (digits) => {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const isSubmitting = addPhoneStatus === "loading";
  const canSubmit = phoneInput.replace(/\D/g, "").length === 10 && consentChecked && !isSubmitting;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      contentAreaStyles="md:!max-w-[440px]"
      bodyClassName="!py-0"
      bodyStyle={{ padding: "0" }}
    >
      <div className="px-6 pt-6 pb-8 flex flex-col gap-5">
        {/* Icon + heading */}
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-12 h-12 rounded-full bg-[#1b2b48] flex items-center justify-center">
            <Phone size={22} weight="fill" className="text-white" />
          </div>
          <h2 className="text-[18px] font-semibold text-[#1b2b48] leading-[26px]">{copy.headline}</h2>
        </div>

        {/* Phone input */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] font-semibold text-[#1b2b48] uppercase tracking-wide">Phone number</label>
          <div className="flex items-center border border-gray-300 rounded-[10px] overflow-hidden h-[46px] focus-within:border-[#1b2b48] transition-colors">
            <span className="px-3 text-[14px] font-semibold text-[#1b2b48] border-r border-gray-300 h-full flex items-center select-none flex-shrink-0 bg-gray-50">
              🇺🇸 +1
            </span>
            <input
              type="tel"
              inputMode="numeric"
              value={formatDisplay(phoneInput)}
              onChange={handlePhoneChange}
              placeholder="(555) 000-0000"
              className="px-3 text-[16px] text-[#1b2b48] placeholder-gray-400 outline-none bg-white flex-1 min-w-0"
              disabled={isSubmitting}
              autoFocus
            />
          </div>
          {validationError && (
            <p className="text-[12px] text-red-500" role="alert">
              {validationError}
            </p>
          )}
        </div>

        {/* Consent checkbox */}
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => {
              setConsentChecked(e.target.checked);
              trackAnalytics(EVENT_NAMES.CLICKED_BUTTON, {
                [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.CHECKBOX,
                [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.PHONE_COLLECTION_CONSENT_CHECKBOX,
                [EVENT_PARAMETERS.TARGET_CHECKBOX_STATE]: e.target.checked ? "checked" : "unchecked",
              });
            }}
            className="mt-0.5 cursor-pointer flex-shrink-0 accent-[#1b2b48]"
            disabled={isSubmitting}
          />
          <span className="text-[12px] text-gray-500 leading-[18px]">{PHONE_COLLECTION_CONSENT_TEXT}</span>
        </label>

        {/* Actions */}
        <div className="flex flex-col gap-2.5">
          <button
            className="w-full bg-[#1b2b48] text-white text-[14px] font-semibold py-[13px] rounded-[10px] disabled:opacity-50 hover:bg-[#243a61] active:bg-[#152238] transition-colors"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isSubmitting ? "Saving…" : "Add number"}
          </button>
          <button
            className="w-full text-[13px] text-gray-400 hover:text-gray-600 transition-colors py-1"
            onClick={handleClose}
          >
            {copy.dismiss}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PhoneCollectionModal;
